import { formatDateRu, formatKg, type CrewTask, type PhotoKind } from '@pms/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuthStore } from '../../auth/store.js';
import { NumbersStep } from '../../reports/NumbersStep.js';
import type { ReportDraft } from '../../reports/draft.js';
import { request } from '../../sync/api.js';
import { syncNow } from '../../sync/engine.js';
import { enqueue } from '../../sync/outbox.js';

/**
 * Журнал сменных отчётов и корректировка.
 *
 * Отчёт не правится: администратор создаёт новый со ссылкой на исходный
 * и обязательной причиной. Старый остаётся в истории — видно, что было,
 * что стало и почему.
 */

interface ReportRow {
  id: string;
  date: string;
  shiftNumber: number;
  site: { id: string; code: string; name: string };
  machine: { id: string; code: string; name: string };
  author: { id: string; fullName: string };
  participants: Array<{ user: { id: string; fullName: string }; shareBp: number }>;
  status: string;
  photoStatus: string;
  occurredAt: string;
  comment: string | null;
  correctionReason: string | null;
  supersedesId: string | null;
  batchesCount: number | null;
  recipeId: string | null;
  outputs: Array<{ productSize: { id: string; name: string }; qtyPieces: number; defectPieces: number }>;
  crewWork: { palletsPacked: number; palletsLoaded: number; tasks: CrewTask[] } | null;
  photos: Array<{ id: string; kind: PhotoKind }>;
  materials: Array<{
    materialType: { id: string; name: string };
    direction: 'PRODUCED' | 'CONSUMED';
    qtyG: string;
  }>;
  incidents: Array<{
    kind: string;
    durationMinutes: number | null;
    comment: string | null;
    trainee: { id: string; fullName: string } | null;
  }>;
}

const PHOTO_LABEL: Record<string, string> = {
  NONE: 'не прикреплено',
  PENDING: 'ожидает загрузки',
  UPLOADED: 'загружено',
};

const STATUS_LABEL: Record<string, string> = {
  REPORTED: 'сдан',
  ACCEPTED: 'принят',
  CORRECTED: 'исправлен',
  SUPERSEDED: 'заменён',
};

/** Собираем из отчёта черновик, чтобы переиспользовать те же формы ввода. */
function draftFromReport(report: ReportRow): ReportDraft {
  return {
    id: crypto.randomUUID(),
    siteId: report.site.id,
    machineId: report.machine.id,
    date: report.date,
    shiftNumber: report.shiftNumber,
    participants: report.participants.map((participant) => ({
      userId: participant.user.id,
      shareBp: participant.shareBp,
    })),
    step: 0,
    outputs: Object.fromEntries(
      report.outputs.map((output) => [output.productSize.id, output.qtyPieces]),
    ),
    defects: Object.fromEntries(
      report.outputs.map((output) => [output.productSize.id, output.defectPieces]),
    ),
    materials: Object.fromEntries(
      report.materials.map((material) => [
        material.materialType.id,
        { direction: material.direction, qtyG: Number(material.qtyG) },
      ]),
    ),
    batchesCount: report.batchesCount,
    recipeId: report.recipeId,
    incidents: [],
    comment: report.comment,
    // Снимки и работу бригады переносим из исходного отчёта: корректируются
    // цифры, а не фотографии, и без них исправление не прошло бы проверку
    crewWork: report.crewWork,
    photos: report.photos,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export function AdminReportsScreen(): JSX.Element {
  const user = useAuthStore((state) => state.user);
  const [reports, setReports] = useState<ReportRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ReportDraft | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const response = await request<{ reports: ReportRow[] }>(
        '/api/reports?limit=100&includeSuperseded=true',
        { method: 'GET' },
        20_000,
      );
      setReports(response.reports);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить отчёты');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!reports) {
    return (
      <div className="min-h-screen px-4 py-6">
        <p className="text-lg text-dim">{error ?? 'Загружаю…'}</p>
        <Link to="/admin" className="mt-4 inline-block rounded-xl bg-muted px-4 py-3 text-base">
          Назад
        </Link>
      </div>
    );
  }

  const open = reports.find((report) => report.id === openId) ?? null;

  const submitCorrection = async (): Promise<void> => {
    if (!user || !open || !draft) return;
    if (reason.trim().length < 3) return;

    setBusy(true);
    try {
      await enqueue({
        type: 'SHIFT_REPORT_CORRECTED',
        aggregateType: 'SHIFT_REPORT',
        aggregateId: draft.id,
        userId: user.id,
        correctionReason: reason.trim(),
        payload: {
          reportId: draft.id,
          siteId: draft.siteId,
          machineId: draft.machineId,
          date: draft.date,
          shiftNumber: draft.shiftNumber,
          participants: draft.participants,
          outputs: Object.entries(draft.outputs).map(([productSizeId, qtyPieces]) => ({
            productSizeId,
            qtyPieces,
            defectPieces: draft.defects[productSizeId] ?? 0,
          })),
          materials: Object.entries(draft.materials).map(([materialTypeId, entry]) => ({
            materialTypeId,
            direction: entry.direction,
            qtyG: entry.qtyG,
          })),
          batchesCount: draft.batchesCount,
          recipeId: draft.recipeId,
          incidents: [],
          crewWork: draft.crewWork,
          comment: draft.comment,
          photos: draft.photos,
          correctsReportId: open.id,
        },
      });
      await syncNow();
      setDraft(null);
      setReason('');
      setOpenId(null);
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (open && draft) {
    return (
      <div className="min-h-screen px-4 py-6">
        <header className="mb-4">
          <h1 className="text-xl font-semibold">Исправление отчёта</h1>
          <p className="mt-1 text-base text-dim">
            {open.site.name} · {open.machine.name} · {formatDateRu(open.date)} · смена{' '}
            {open.shiftNumber}
          </p>
        </header>

        <p className="mb-4 rounded-xl bg-card shadow-card p-4 text-base text-body">
          Исходный отчёт останется в истории. Новые цифры заменят его в остатках и аналитике.
        </p>

        <NumbersStep
          draft={draft}
          siteCode={open.site.code}
          onChange={(patch) => setDraft({ ...draft, ...patch })}
        />

        <label className="mt-6 flex flex-col gap-2 text-base">
          Причина исправления (обязательно)
          <textarea
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Например: оператор посчитал на 10 плит больше"
            className="rounded-xl bg-canvas px-4 py-3 text-lg outline-none focus:ring-2 focus:ring-sky-500"
          />
        </label>

        <div className="mt-6 flex gap-touch">
          <button
            type="button"
            onClick={() => {
              setDraft(null);
              setReason('');
            }}
            className="flex-1 rounded-xl bg-muted px-5 py-4 text-lg font-semibold active:bg-line"
          >
            Отмена
          </button>
          <button
            type="button"
            disabled={busy || reason.trim().length < 3}
            onClick={() => void submitCorrection()}
            className="flex-1 rounded-xl bg-emerald-600 px-5 py-4 text-lg font-semibold text-white active:bg-emerald-700 disabled:opacity-40"
          >
            {busy ? 'Отправляю…' : 'Сохранить исправление'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Сменные отчёты</h1>
        <Link to="/admin" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      {reports.length === 0 ? (
        <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">Отчётов пока нет</p>
      ) : (
        <ul className="flex flex-col gap-touch">
          {reports.map((report) => {
            const superseded = report.status === 'SUPERSEDED';
            const isOpen = report.id === openId;

            return (
              <li
                key={report.id}
                className={`rounded-xl p-4 ${superseded ? 'bg-canvas opacity-70' : 'bg-card shadow-card'}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : report.id)}
                  className="w-full text-left"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-lg font-semibold">
                      {report.site.name} · {report.machine.name}
                    </span>
                    <span className="text-base text-dim">
                      {formatDateRu(report.date)} · смена {report.shiftNumber}
                    </span>
                  </div>
                  <p className="mt-1 text-base text-body">
                    {report.author.fullName} · {STATUS_LABEL[report.status] ?? report.status} · фото:{' '}
                    {PHOTO_LABEL[report.photoStatus] ?? report.photoStatus}
                  </p>
                </button>

                {isOpen && (
                  <div className="mt-4 border-t border-line pt-4">
                    <ul className="flex flex-col gap-1 text-base">
                      {report.outputs.map((output) => (
                        <li key={output.productSize.id} className="flex justify-between">
                          <span className="text-body">{output.productSize.name}</span>
                          <span className="tabular-nums">{output.qtyPieces} шт</span>
                        </li>
                      ))}
                      {report.batchesCount !== null && (
                        <li className="flex justify-between">
                          <span className="text-body">Замесов</span>
                          <span className="tabular-nums">{report.batchesCount}</span>
                        </li>
                      )}
                      {report.materials.map((material) => (
                        <li key={material.materialType.id} className="flex justify-between">
                          <span className="text-body">
                            {material.direction === 'PRODUCED' ? 'Произведено' : 'Расход'}:{' '}
                            {material.materialType.name}
                          </span>
                          <span className="tabular-nums">{formatKg(Number(material.qtyG))} кг</span>
                        </li>
                      ))}
                    </ul>

                    {report.incidents.length > 0 && (
                      <p className="mt-3 text-base text-body">
                        События:{' '}
                        {report.incidents
                          .map(
                            (incident) =>
                              `${incident.kind}${incident.durationMinutes ? ` ${incident.durationMinutes} мин` : ''}`,
                          )
                          .join(', ')}
                      </p>
                    )}
                    {report.comment && (
                      <p className="mt-2 text-base text-body">{report.comment}</p>
                    )}
                    {report.correctionReason && (
                      <p className="mt-2 text-base text-amber-700">
                        Исправление: {report.correctionReason}
                      </p>
                    )}
                    <p className="mt-2 text-base text-faint">
                      Состав: {report.participants.map((item) => item.user.fullName).join(', ')}
                    </p>

                    {!superseded && (
                      <button
                        type="button"
                        onClick={() => setDraft(draftFromReport(report))}
                        className="mt-4 rounded-xl bg-sky-600 px-5 py-3 text-base font-semibold text-white active:bg-sky-700"
                      >
                        Исправить
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
